import type { ReactNode } from 'react';
import memoizeOne from 'memoize-one';
import type {
	ActiveFieldHeaderInfo,
	ActiveFieldHeaderInfoLevel,
	GetActiveFieldHeaderInfo,
} from '../../types.tsx';

type NonNullableActiveFieldHeaderInfo = NonNullable<ActiveFieldHeaderInfo>;

const levelToNumber = (level: ActiveFieldHeaderInfoLevel | undefined): number => {
	switch (level) {
		case 'error':
			return 3;
		case 'warning':
			return 2;
		case 'info':
			return 1;
		default:
			return 0;
	}
};

/**
 * Same as what Array.prototype.sort requires.
 * And reverse as Java's compare method, because
 * we want to sort descending due to undefined
 * values being sent to the end of an array
 * by default in Array.prototype.sort method.
 *
 * Returns:
 * 0: if (x==y)
 * -1: if (x > y)
 * 1: if (x < y)
 */
export const reversedCompare = (
	x: ActiveFieldHeaderInfoLevel | undefined,
	y: ActiveFieldHeaderInfoLevel | undefined,
): number => {
	if (levelToNumber(x) < levelToNumber(y)) {
		return 1;
	}
	if (levelToNumber(x) > levelToNumber(y)) {
		return -1;
	}
	return 0;
};

type AlwaysDefinedGetActiveFieldHeaderInfo = (
	params: Parameters<GetActiveFieldHeaderInfo>[0],
) => NonNullableActiveFieldHeaderInfo;

function paramsEqual(
	[params1]: [Parameters<GetActiveFieldHeaderInfo>[0]],
	[params2]: [Parameters<GetActiveFieldHeaderInfo>[0]],
): boolean {
	// We don't compare intl on purpose because we do not care about equality of that
	// - the intl instance can change as much as it likes, and it will not affect our memoizer.
	return (
		params1.associatedField === params2.associatedField &&
		params1.isRequirabilityDisplayed === params2.isRequirabilityDisplayed &&
		params1.layoutOwners === params2.layoutOwners
	);
}

/**
 * On the reducer output, we always use longText as an array.
 * Array is a ReactNode too and it makes all the pushing and concatenating easier.
 */
type Accumulator = ActiveFieldHeaderInfo & {
	longText: Array<ReactNode>;
};

/**
 * This must be a function because the reducer uses the longText array as a writeable array
 * therefore we need new instances of it for every run.
 * The whole function is memoized on the top level.
 */
const initialValue = (): Accumulator => ({
	longText: [],
});

function reducer(mostImportantLevel: 'info' | 'warning' | 'error' | undefined) {
	return (acc: Accumulator, curr: ActiveFieldHeaderInfo): Accumulator => {
		if (curr == null) {
			return acc;
		}
		if (Array.isArray(curr.longText)) {
			acc.longText.splice(acc.longText.length, 0, ...curr.longText);
		} else if (curr.longText != null) {
			acc.longText.push(curr.longText);
		}
		if (
			acc.icon == null &&
			acc.shortText == null &&
			acc.midText == null &&
			curr.icon?.type === mostImportantLevel
		) {
			acc.icon = curr.icon;
			acc.shortText = curr.shortText;
			acc.midText = curr.midText;
		}
		return acc;
	};
}

function getMostImportantLevel(
	notEmptyResults: NonNullableActiveFieldHeaderInfo[],
): ActiveFieldHeaderInfoLevel | undefined {
	return notEmptyResults
		.map((i) => i.icon?.type)
		.sort(reversedCompare)
		.shift();
}

/**
 * Merges multiple getters into one. Useful if you want to have your code split to separate independent functions.
 *
 * It will merge by the following logic:
 * 1. All long texts will display - there's enough space for all
 * 2. Other properties do not have enough space so we discard some.
 * 2a. Errors win - so whatever has `icon: error` is going to suppress others
 * 2b. Warnings second - `icon: warning` will hide all info lozenges
 * 2c. Info third
 * 2d. undefined is last - means that messages without an icon have the lowest priority
 * 3. First come, first served. So the first error is going to suppress all following errors.
 *
 * This means that one of your getters might return shortText or midText but platform code never renders it.
 *
 * Never returns undefined - it will return an empty object instead.
 */
export const mergeGetActiveFieldHeaderInfo = (
	...getters: ReadonlyArray<GetActiveFieldHeaderInfo>
): AlwaysDefinedGetActiveFieldHeaderInfo =>
	memoizeOne((params: Parameters<GetActiveFieldHeaderInfo>[0]): ActiveFieldHeaderInfo => {
		const results = getters.map((getter) => getter(params));
		const notEmptyResults: NonNullableActiveFieldHeaderInfo[] = results.filter(Boolean);
		if (notEmptyResults.length === 0) {
			return initialValue();
		}
		if (notEmptyResults.length === 1) {
			return notEmptyResults[0];
		}
		const mostImportantLevel = getMostImportantLevel(notEmptyResults);
		return notEmptyResults.reduce<Accumulator>(reducer(mostImportantLevel), initialValue());
	}, paramsEqual);
